import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme, alpha } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PsychologyIcon from "@mui/icons-material/Psychology";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ProfSelector from "components/ProfSelector";
import {
  listQuizInsightsCall,
  createQuizInsightCall,
  deleteQuizInsightCall,
  listQuizAssignmentsCall,
} from "../../redux/api/quizSystem.api";

function formatDate(d, t) {
  if (!d) return t("pages.quizInsights.dateEmpty");
  return new Date(d).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function scopeTypeLabel(type, t) {
  const k = String(type || "all").toLowerCase();
  if (k === "quiz") return t("pages.quizInsights.scopeTypeQuiz");
  if (k === "assignment") return t("pages.quizInsights.scopeTypeAssignment");
  return t("pages.quizInsights.scopeTypeAll");
}

function renderInline(text, theme) {
  const parts = String(text).split(/\*\*([^*]+)\*\*/g);
  if (parts.length === 1) {
    return text;
  }
  const nodes = [];
  parts.forEach((part, idx) => {
    if (idx % 2 === 1) {
      // bold segment
      nodes.push(
        <Box
          key={`b-${idx}`}
          component="span"
          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
        >
          {part}
        </Box>
      );
    } else if (part) {
      nodes.push(
        <Box key={`t-${idx}`} component="span">
          {part}
        </Box>
      );
    }
  });
  return nodes;
}

function renderInsightSummary(text, theme, t) {
  if (!text) {
    return (
      <MDTypography variant="body2" color="text.secondary">
        {t("pages.quizInsights.noSummary")}
      </MDTypography>
    );
  }

  const lines = String(text).split(/\r?\n/);
  const elements = [];
  let currentBullets = [];

  const flushBullets = () => {
    if (!currentBullets.length) return;
    elements.push(
      <Box
        key={`bullets-${elements.length}`}
        component="ul"
        sx={{ pl: 3, mb: 1.5, mt: 0.5 }}
      >
        {currentBullets.map((content, idx) => (
          <Box key={idx} component="li" sx={{ mb: 0.5 }}>
            <MDTypography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.7 }}
            >
              {renderInline(content, theme)}
            </MDTypography>
          </Box>
        ))}
      </Box>
    );
    currentBullets = [];
  };

  for (let idx = 0; idx < lines.length; idx += 1) {
    const raw = lines[idx];
    const next = idx + 1 < lines.length ? String(lines[idx + 1]).trim() : "";
    const line = raw.trim();
    if (!line) {
      flushBullets();
      continue;
    }

    // Markdown-style heading: ### Title
    if (line.startsWith("### ")) {
      flushBullets();
      const title = line.replace(/^###\s*/, "");
      elements.push(
        <MDTypography
          key={`h-${idx}`}
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            mt: elements.length ? 2 : 0.5,
            mb: 0.5,
            color: theme.palette.primary.main,
          }}
        >
          {title}
        </MDTypography>
      );
      continue;
    }

    // Plain heading line (no ###, no period, short, followed by content)
    const looksLikeHeading =
      !/[:.?]$/.test(line) &&
      line.length <= 80 &&
      next &&
      !/^\d+\.\s+/.test(next) &&
      !/^-\s+/.test(next);
    if (looksLikeHeading) {
      flushBullets();
      elements.push(
        <MDTypography
          key={`h-${idx}`}
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            mt: elements.length ? 2 : 0.5,
            mb: 0.5,
            color: theme.palette.primary.main,
          }}
        >
          {line}
        </MDTypography>
      );
      continue;
    }

    // Numbered, dash, or bold bullet line
    if (/^\d+\.\s+/.test(line) || /^-\s+/.test(line) || line.startsWith("**")) {
      const content = line.replace(/^\d+\.\s+/, "").replace(/^-\s+/, "");
      currentBullets.push(content);
      continue;
    }

    // Regular paragraph
    flushBullets();
    elements.push(
      <MDTypography
        key={`p-${idx}`}
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, lineHeight: 1.7 }}
      >
        {renderInline(line, theme)}
      </MDTypography>
    );
  }

  flushBullets();
  return elements;
}

export default function QuizInsights() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [profId, setProfId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [scopeType, setScopeType] = useState("all");
  const [requestTitle, setRequestTitle] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [assignmentId, setAssignmentId] = useState("");

  const loadInsights = async () => {
    setLoading(true);
    try {
      const params = {};
      if (profId) params.prof_id = profId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (scopeType) params.scope_type = scopeType;
      const res = await listQuizInsightsCall(params);
      const data = res?.data ?? res;
      setInsights(Array.isArray(data) ? data : []);
    } catch (_) {
      setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  // Load quizzes/assignments for the selected professor (or current prof)
  useEffect(() => {
    const loadAssignments = async () => {
      try {
        const params = profId ? { prof_id: profId } : {};
        const res = await listQuizAssignmentsCall(params);
        const list = res?.data ?? [];
        setAssignments(Array.isArray(list) ? list : []);
      } catch (_) {
        setAssignments([]);
      }
    };
    loadAssignments();
  }, [profId]);

  useEffect(() => {
    loadInsights();
  }, [profId, scopeType, dateFrom, dateTo]);

  const handleRequestAnalysis = async () => {
    setCreating(true);
    try {
      const body = {
        scope_type: scopeType,
        title: requestTitle || undefined,
        prof_id: profId || undefined,
        scope_id:
          scopeType === "quiz" && assignmentId ? assignmentId : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      await createQuizInsightCall(body);
      setRequestTitle("");
      await loadInsights();
    } catch (_) {
      // keep list as is
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteQuizInsightCall(id);
      setInsights((prev) => prev.filter((i) => i.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (_) {}
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const cardBg =
    theme.palette.background?.card ??
    theme.palette.background.paper ??
    theme.palette.background.default;
  const primaryMain = theme.palette.primary.main;
  const gradientBg = `linear-gradient(135deg, ${alpha(
    primaryMain,
    0.08
  )} 0%, ${alpha(primaryMain, 0.02)} 100%)`;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        py={3}
        sx={{
          minHeight: "100vh",
          borderRadius: 3,
          background: theme.palette.mode === "dark" ? undefined : gradientBg,
        }}
      >
        <MDBox width="100%" maxWidth="100%" px={2} boxSizing="border-box">
          {/* Hero header */}
          <MDBox
            mb={4}
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            gap={3}
            flexWrap="wrap"
          >
            <MDBox display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${primaryMain}, ${
                    theme.palette.primary.dark || primaryMain
                  })`,
                  boxShadow: `0 8px 24px ${alpha(primaryMain, 0.35)}`,
                }}
              >
                <PsychologyIcon
                  style={{
                    width: 40,
                    height: 40,
                  }}
                  sx={{ color: "#ffffff" }}
                />
              </Box>
              <MDBox>
                <MDTypography
                  variant="h3"
                  fontWeight="bold"
                  color="text.primary"
                >
                  {t("pages.quizInsights.title")}
                </MDTypography>
                <MDTypography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("pages.quizInsights.subtitle")}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Filters + Request */}
          <Card
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              bgcolor: cardBg,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[2],
            }}
          >
            <MDTypography
              variant="button"
              fontWeight="bold"
              color="text.primary"
              sx={{ display: "block", mb: 2 }}
            >
              {t("pages.quizInsights.filtersSectionTitle")}
            </MDTypography>
            <MDBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              gap={2}
              flexWrap="wrap"
            >
              <ProfSelector
                value={profId}
                onChange={setProfId}
                includeMyContent
              />
              <FormControl
                size="small"
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": { minHeight: 44 },
                }}
              >
                <InputLabel>{t("pages.quizInsights.scopeLabel")}</InputLabel>
                <Select
                  value={scopeType}
                  label={t("pages.quizInsights.scopeLabel")}
                  onChange={(e) => {
                    setScopeType(e.target.value);
                    if (e.target.value !== "quiz") {
                      setAssignmentId("");
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: { "& .MuiMenuItem-root": { minHeight: 44, py: 1.5 } },
                    },
                  }}
                >
                  <MenuItem value="all">{t("pages.quizInsights.scopeAllResults")}</MenuItem>
                  <MenuItem value="quiz">{t("pages.quizInsights.scopeSingleQuiz")}</MenuItem>
                  <MenuItem value="assignment">{t("pages.quizInsights.scopeAssignment")}</MenuItem>
                </Select>
              </FormControl>
              {scopeType === "quiz" && (
                <Box sx={{ minWidth: 260 }}>
                  <Autocomplete
                    size="small"
                    options={assignments}
                    value={
                      assignmentId
                        ? assignments.find((a) => a.id === assignmentId) || null
                        : null
                    }
                    getOptionLabel={(opt) =>
                      opt?.title || opt?.access_code || opt?.id || ""
                    }
                    onChange={(_, newValue) => {
                      setAssignmentId(newValue?.id || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("pages.quizInsights.quizLabel")}
                        placeholder={t("pages.quizInsights.quizSearchPlaceholder")}
                        sx={{
                          "& .MuiOutlinedInput-root": { minHeight: 44 },
                        }}
                      />
                    )}
                    ListboxProps={{
                      sx: {
                        maxHeight: 320,
                        "& .MuiAutocomplete-option": {
                          minHeight: 44,
                          py: 1.25,
                        },
                      },
                    }}
                    clearOnEscape
                  />
                </Box>
              )}
              <TextField
                size="small"
                label={t("pages.quizInsights.dateFrom")}
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label={t("pages.quizInsights.dateTo")}
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label={t("pages.quizInsights.analysisTitleOptional")}
                placeholder={t("pages.quizInsights.analysisTitlePlaceholder")}
                value={requestTitle}
                onChange={(e) => setRequestTitle(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <MDButton
                variant="gradient"
                color="primary"
                onClick={handleRequestAnalysis}
                disabled={creating}
                startIcon={creating ? null : <AutoAwesomeIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  boxShadow: `0 4px 14px ${alpha(primaryMain, 0.4)}`,
                }}
              >
                {creating ? t("pages.quizInsights.analyzing") : t("pages.quizInsights.requestNewAnalysis")}
              </MDButton>
            </MDBox>
          </Card>

          {/* List */}
          <MDTypography
            variant="h6"
            fontWeight="bold"
            color="text.primary"
            sx={{ mb: 2 }}
          >
            {t("pages.quizInsights.yourAnalyses")}
          </MDTypography>
          {loading ? (
            <MDBox py={4} textAlign="center">
              <MDTypography color="text.secondary">{t("pages.quizInsights.loading")}</MDTypography>
            </MDBox>
          ) : insights.length === 0 ? (
            <Card
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: cardBg,
                border: `1px dashed ${theme.palette.divider}`,
                textAlign: "center",
              }}
            >
             
              <MDTypography variant="body1" color="text.secondary">
                {t("pages.quizInsights.emptyState")}
              </MDTypography>
            </Card>
          ) : (
            <MDBox display="flex" flexDirection="column" gap={2}>
              {insights.map((insight) => (
                <Card
                  key={insight.id}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    bgcolor: cardBg,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[1],
                    transition: "box-shadow 0.2s, border-color 0.2s",
                    "&:hover": {
                      boxShadow: theme.shadows[4],
                      borderColor: alpha(primaryMain, 0.3),
                    },
                  }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ p: 2, cursor: "pointer" }}
                    onClick={() => toggleExpand(insight.id)}
                  >
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={2}
                      flex={1}
                      minWidth={0}
                    >
                      <Chip
                        size="small"
                        label={scopeTypeLabel(insight.scope_type, t)}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                      <MDTypography
                        variant="body1"
                        fontWeight="600"
                        color="text.primary"
                        noWrap
                      >
                        {insight.title || t("pages.quizInsights.defaultInsightTitle")}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        {formatDate(insight.created_at, t)}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(insight.id)}
                        aria-label={t("pages.quizInsights.ariaDelete")}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={
                          expandedId === insight.id
                            ? t("pages.quizInsights.ariaCollapse")
                            : t("pages.quizInsights.ariaExpand")
                        }
                      >
                        {expandedId === insight.id ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </MDBox>
                  </MDBox>
                  <Collapse in={expandedId === insight.id}>
                    <MDBox
                      sx={{
                        px: 2,
                        pb: 2,
                        pt: 1,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? alpha(primaryMain, 0.08)
                            : alpha(primaryMain, 0.02),
                        borderRadius: 0,
                        borderLeft: `3px solid ${alpha(primaryMain, 0.45)}`,
                        mt: -0.25,
                      }}
                    >
                      <Box sx={{ fontFamily: "inherit" }}>
                        {renderInsightSummary(insight.summary, theme, t)}
                      </Box>
                    </MDBox>
                  </Collapse>
                </Card>
              ))}
            </MDBox>
          )}
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
